// gameStatus.js — Pure utility functions extracted from WeeklyLineupManager
import { SUPER_DOG_SPREAD } from './season';

import { weeklyLineupUtils } from './weeklyLineupUtils';
import { normalizeTeamName } from './teamName';

// Builds a per-team-slug map of this week's game from a `games` rows array,
// perspective-swapped so the same shape works for either side (home/away).
// This is the source of truth for schedule/score/spread, not
// team_season_stats.next_opponent, which lags behind `games`: `games` is
// populated for the whole season by backend/schedule.js as soon as CFBD
// publishes it, while team_season_stats.next_opponent is written by the
// espn.js cron and stays empty until games start reporting live/final data.
// Scoreboard and the Lineup tab both call this so they never disagree about
// the same week.
export const buildGamesBySlug = (gamesData) => {
  const map = {};
  (gamesData || []).forEach((g) => {
    if (!g.home_team || !g.away_team) return;
    const homeSlug = normalizeTeamName(g.home_team);
    const awaySlug = normalizeTeamName(g.away_team);
    const shared = {
      gameComplete: g.game_complete || false,
      gameStatus: g.game_status || 'scheduled',
      period: g.period,
      clock: g.clock,
      date: g.date,
      startTimeTbd: Boolean(g.start_time_tbd),
    };
    // homeTeam/awayTeam are perspective-swapped slugs (homeTeam is always
    // "this team", not necessarily the real home team) for identity checks.
    // games.home_team/away_team are already slugs (e.g. "se-louisiana"), not
    // display names, so getGameDisplayInfo resolves the opponent's real
    // school name through an allTeams map rather than from these fields.
    //
    // Because of that swap, `homeTeam === teamName` is true for BOTH entries
    // and cannot answer "is this team at home". `isHome` is the only field
    // that can. Scores and homeSpread are already swapped to this team's
    // perspective, so they must NOT be re-swapped by consulting isHome.
    map[homeSlug] = {
      ...shared,
      homeTeam: homeSlug, awayTeam: awaySlug,
      isHome: true,
      homeScore: g.home_score || 0, awayScore: g.away_score || 0,
      homeSpread: g.home_spread,
    };
    map[awaySlug] = {
      ...shared,
      homeTeam: awaySlug, awayTeam: homeSlug,
      isHome: false,
      homeScore: g.away_score || 0, awayScore: g.home_score || 0,
      homeSpread: typeof g.home_spread === 'number' ? -g.home_spread : g.home_spread,
    };
  });
  return map;
};

// Whether a game is live right now: in_progress or live, or 'delayed' with a
// period already recorded. ESPN reports a mid-game weather or other delay
// with the same 'delayed' status as a delay pushing back a kickoff that
// hasn't happened yet, and the two must not read the same way: one is still
// a live game, paused, and the other hasn't started. `period` is what tells
// them apart, since it is only ever set once a game has actually kicked off.
// Every consumer of "is this live" (the League tab's starter dots, TeamLogo,
// this file's own outcome grading below) shares this so a fix here reaches
// all of them instead of drifting between separately-written checks, which
// is exactly what happened before this existed: 'delayed' silently fell
// through to "not live" everywhere that only checked for 'in_progress'.
export const isGameLive = (gameInfo) =>
  !!gameInfo && (
    gameInfo.gameStatus === 'in_progress' ||
    gameInfo.gameStatus === 'live' ||
    (gameInfo.gameStatus === 'delayed' && typeof gameInfo.period === 'number' && gameInfo.period > 0)
  );

// Outcome category for a team's game: win/loss crossed with covered/didn't
// cover/push, plus 'upcoming' and 'bye'. Live games are scored provisionally
// against the current score, same as a final, since this is a highlights
// page reacting to what's happening right now. Cover math matches
// backend/scoring.js's calculateTeamFantasyPoints: negative spread means
// favoured, coverPoints = actualMargin - |spread| when favoured,
// actualMargin + spread when underdog, |coverPoints| < 0.5 is a push.
export const getOutcomeCategory = (gameInfo, teamName) => {
  if (!gameInfo) return 'bye';

  const isHome = gameInfo.homeTeam === teamName;
  const myScore = isHome ? gameInfo.homeScore : gameInfo.awayScore;
  const opponentScore = isHome ? gameInfo.awayScore : gameInfo.homeScore;
  const hasStarted = gameInfo.gameComplete || isGameLive(gameInfo);
  if (!hasStarted) return 'upcoming';

  const won = myScore > opponentScore;
  const spread = isHome ? gameInfo.homeSpread : (typeof gameInfo.homeSpread === 'number' ? -gameInfo.homeSpread : gameInfo.homeSpread);

  if (typeof spread !== 'number') return won ? 'win-nocover' : 'loss-nocover';

  const actualMargin = myScore - opponentScore;
  const coverPoints = spread < 0 ? actualMargin - Math.abs(spread) : spread > 0 ? actualMargin + spread : actualMargin;

  if (Math.abs(coverPoints) < 0.5) return 'push';
  return won
    ? (coverPoints > 0 ? 'win-cover' : 'win-nocover')
    : (coverPoints > 0 ? 'loss-cover' : 'loss-nocover');
};

/**
 * One spread, from one team's point of view, as text: "-14.5", "+7", "PICK".
 *
 * Negative means favoured, matching calculateTeamFantasyPoints. Exported so a
 * caller that has already worked out whose side the number is on does not have
 * to reproduce the sign rule: the team name normalizer grew eleven divergent
 * copies that way.
 */
export const formatSpreadValue = (spread) => {
  if (typeof spread !== 'number') return null;
  if (spread === 0) return 'PICK';
  return spread > 0 ? `+${spread}` : `${spread}`;
};

/**
 * The spread as a number, from one team's point of view, or null when the
 * game has no line. Negative means favoured.
 */
export const getTeamSpread = (gameInfo, teamName) => {
  if (!gameInfo || typeof gameInfo.homeSpread !== 'number') return null;
  return gameInfo.homeTeam === teamName ? gameInfo.homeSpread : -gameInfo.homeSpread;
};

/**
 * Whether this team is a Super Dog: getting SUPER_DOG_SPREAD points or more,
 * which means it takes no penalty for losing.
 *
 * Shared so the lineup and the scoreboard mark it from the same rule the
 * scoring page states. Reading it off the formatted string instead would put
 * a second copy of the threshold in the UI, and this is exactly the kind of
 * number that must not drift.
 */
export const isSuperDog = (gameInfo, teamName) => {
  const spread = getTeamSpread(gameInfo, teamName);
  return spread !== null && spread >= SUPER_DOG_SPREAD;
};

export const getGameSpreadDisplay = (gameInfo, teamName) =>
  formatSpreadValue(getTeamSpread(gameInfo, teamName));

/**
 * "Q3", "OT", "2OT" for a games.period value, or "" when there is no period.
 *
 * Shared so the ticker and the team status chip agree. The ticker showed only
 * a clock, which read as "LIVE · 13:08" with no way to tell a first quarter
 * from a fourth.
 *
 * ESPN counts overtime as period 5 onward, so 5 is the first OT and 6 is 2OT.
 */
export const formatPeriod = (period) => {
  if (!period) return '';
  if (period <= 4) return `Q${period}`;
  if (period === 5) return 'OT';
  return `${period - 4}OT`;
};

// allTeams is the normalized teams map from AuthContext (slug -> team),
// used to resolve the opponent's real school name for display. Falls back
// to the raw slug if the map isn't available or doesn't have the team, so
// this degrades gracefully rather than throwing.
// Game states that are neither upcoming, live nor finished. Written by
// backend/espn.js from ESPN's status vocabulary. 'postponed' covers cancelled
// too: from a manager's point of view both mean no football and no points.
const INTERRUPTED_DISPLAY = {
  delayed: 'Delayed',
  postponed: 'Postponed',
};

export const getGameDisplayInfo = (gameInfo, teamName, allTeams) => {
  if (!gameInfo) {
    return {
      type: 'bye-week',
      display: 'BYE WEEK',
      chipClass: 'bg-tripplay-bg text-tripplay-text border border-hairline',
      subtext: 'No game this week',
      isLive: false,
    };
  }

  const isHome = gameInfo.homeTeam === teamName;
  const myScore = isHome ? gameInfo.homeScore : gameInfo.awayScore;
  const opponentScore = isHome ? gameInfo.awayScore : gameInfo.homeScore;
  const opponentSlug = isHome ? gameInfo.awayTeam : gameInfo.homeTeam;
  const opponent = allTeams?.[opponentSlug]?.school || opponentSlug;
  // Real home/away. Two input shapes reach here: a buildGamesBySlug entry,
  // which swaps homeTeam to always equal teamName and carries an explicit
  // `isHome`, and a raw home/away pair. The `isHome` above answers only the
  // raw shape, so every swapped entry read as home and said "vs", including
  // for road games. Prefer the explicit field, fall back to the comparison.
  const venuePrefix = (typeof gameInfo.isHome === 'boolean' ? gameInfo.isHome : isHome) ? 'vs' : '@';

  if (gameInfo.gameComplete) {
    const won = myScore > opponentScore;
    return {
      type: 'completed',
      display: `${myScore}-${opponentScore} ${won ? 'W' : 'L'}`,
      chipClass: won ? 'bg-success-bg text-success-text border border-success-border' : 'bg-danger-bg text-danger-text border border-danger-border',
      subtext: `${venuePrefix} ${opponent}`,
      isLive: false
    };
  }

  if (gameInfo.gameStatus === 'in_progress' || gameInfo.gameStatus === 'live') {
    const won = myScore > opponentScore;
    const tied = myScore === opponentScore;

    const periodLabel = formatPeriod(gameInfo.period);
    const periodDisplay = periodLabel ? ` ${periodLabel}` : '';

    let clockDisplay = '';
    if (gameInfo.clock && gameInfo.clock !== '0:00' && gameInfo.clock !== '00:00') {
      clockDisplay = ` ${gameInfo.clock}`;
    }

    let resultIndicator = '';
    if (tied) {
      resultIndicator = ' T';
    } else if (won) {
      resultIndicator = ' W';
    } else {
      resultIndicator = ' L';
    }

    return {
      type: 'live',
      display: `${myScore}-${opponentScore}${resultIndicator}${periodDisplay}${clockDisplay}`,
      chipClass: 'bg-success-bg text-success-text border border-success-border animate-pulse',
      subtext: `${venuePrefix} ${opponent} LIVE`,
      // subtext without the LIVE suffix, for a row that states liveness on
      // its status line instead (TeamSlot).
      matchup: `${venuePrefix} ${opponent}`,
      isLive: true,
      period: gameInfo.period,
      clock: gameInfo.clock
    };
  }

  // Delayed, suspended, postponed or abandoned. Before this these fell through
  // to the branch below and rendered as an ordinary upcoming fixture, so a
  // weather-delayed game showed a kickoff time that had already passed while
  // its row was locked and red. On a wet Saturday that is most of the board.
  //
  // A game interrupted after kickoff keeps the score it reached, since that is
  // what it is graded on while it waits. One delayed before kickoff has no
  // score to show and no points, which is why hasStarted is reported rather
  // than inferred from the type.
  if (INTERRUPTED_DISPLAY[gameInfo.gameStatus]) {
    const hasStarted = typeof gameInfo.period === 'number' && gameInfo.period > 0;
    const label = INTERRUPTED_DISPLAY[gameInfo.gameStatus];
    const score = hasStarted ? `${myScore}-${opponentScore} ` : '';

    return {
      type: 'interrupted',
      display: `${score}${label}`,
      chipClass: 'bg-captain-fill text-captain-text border border-captain-border',
      subtext: `${venuePrefix} ${opponent}`,
      isLive: false,
      hasStarted,
      period: gameInfo.period,
    };
  }

  const gameTime = formatGameTimeChip(gameInfo.date);
  return {
    type: 'upcoming',
    display: gameTime || 'TBD',
    chipClass: 'bg-surface-subtle text-ink-muted border border-hairline',
    subtext: `${venuePrefix} ${opponent}`,
    isLive: false
  };
};

// How long before kickoff a team's lineup slot closes.
//
// An hour was arbitrary and cost managers most of the pre-game window for no
// benefit: the point of the lock is to stop a lineup being set after the ball
// is in the air, not to end the decision early.
export const LOCK_LEAD_MS = 15 * 60 * 1000;

// Midnight Eastern on the day a given instant falls, as a real instant.
// Eastern rather than the viewer's zone: the season calendar, the week
// boundaries and the week-0 split are all defined in Eastern, and a manager
// in London must not get a different lock time from one in Dallas.
export function startOfEasternDay(date) {
  const ymd = new Intl.DateTimeFormat("en-CA", {
    timeZone: "America/New_York",
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
  }).format(date);

  // Midnight on that date read as UTC. Shifting it by Eastern's offset ON that
  // date gives the real instant. The offset has to be resolved rather than
  // assumed: Eastern is -04:00 in September and -05:00 in November.
  const utcMidnight = new Date(`${ymd}T00:00:00Z`);
  const offsetLabel = new Intl.DateTimeFormat("en-US", {
    timeZone: "America/New_York",
    timeZoneName: "longOffset",
  })
    .formatToParts(utcMidnight)
    .find((part) => part.type === "timeZoneName").value;

  const match = offsetLabel.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/);
  if (!match) return utcMidnight;

  const sign = match[1] === "-" ? -1 : 1;
  const offsetMinutes = sign * (Number(match[2]) * 60 + Number(match[3] || 0));
  return new Date(utcMidnight.getTime() - offsetMinutes * 60 * 1000);
}


// Kickoff times are TBD for roughly half the season when CFBD first publishes
// them, and those rows carry a placeholder of midnight Eastern on gameday
// rather than a real time. That placeholder is almost always LATER than the
// true kickoff, so a lead time measured back from it can leave a team
// unlocked while it is already playing. A TBD game therefore locks at the
// start of its gameday and stays locked until a real time arrives.
//
// This matters more at 15 minutes than it did at 60, but it was already
// wrong at 60.
const TBD_LOCK_AT_START_OF_DAY = true;

// Synchronous, takes an already-loaded gameInfo (a buildGamesBySlug entry)
// instead of querying for itself. Used to gate captain/5x/cut/move actions,
// so it has to agree with TeamSlot's own display, which reads the same
// gamesBySlug map: this used to go through getTeamGameInfo
// (team_season_stats.next_opponent), which lags `games` until the espn.js
// cron writes it, so a team could show as "not locked" here while the
// schedule card already showed it as live.
export const isTeamLocked = (gameInfo, teamName, currentTime = window.TEST_CURRENT_TIME || new Date()) => {
  try {
    if (!gameInfo || !gameInfo.date) {
      return {
        locked: false,
        reason: null,
        message: null,
        gameInfo: null
      };
    }

    if (gameInfo.gameComplete) {
      return {
        locked: true,
        reason: 'game_completed',
        message: `${teamName} is locked - game completed`,
        gameInfo
      };
    }

    const gameDate = gameInfo.date instanceof Date ? gameInfo.date : new Date(gameInfo.date);

    if (isNaN(gameDate.getTime())) {
      console.error(`Invalid date for ${teamName}:`, gameInfo.date);
      return {
        locked: false,
        reason: 'invalid_date',
        message: `${teamName} lock status unavailable - invalid date`,
        gameInfo
      };
    }

    // A TBD kickoff locks from midnight Eastern on gameday, since the stored
    // time is a placeholder rather than the real one.
    const lockTime =
      TBD_LOCK_AT_START_OF_DAY && gameInfo.startTimeTbd
        ? startOfEasternDay(gameDate)
        : new Date(gameDate.getTime() - LOCK_LEAD_MS);
    if (currentTime >= lockTime) {
      return {
        locked: true,
        reason: gameDate <= currentTime ? 'game_started' : 'lock_time_reached',
        message: `${teamName} is locked - ${gameDate <= currentTime ? 'game started' : 'lineup locked'}`,
        gameInfo,
        lockTime,
        gameDate
      };
    }

    return {
      locked: false,
      reason: null,
      message: null,
      gameInfo,
      lockTime,
      gameDate
    };
  } catch (error) {
    console.error(`Error checking lock status for ${teamName}:`, error);
    return {
      locked: false,
      reason: 'error',
      message: `${teamName} lock status check failed`,
      gameInfo: null
    };
  }
};

export const enhanceTeamWithLiveData = (team) => {
  if (!team) return null;

  return {
    ...team,
    currentSeason: {
      ...team.currentSeason,
      liveGame: team.currentSeason?.liveGame || {
        gameStatus: null,
        homeScore: null,
        awayScore: null,
        quarter: null,
        timeRemaining: null,
        lastUpdated: null,
        possession: null
      }
    },
    lockStatus: {
      locked: false,
      reason: null,
      message: null,
      lockTime: null
    }
  };
};

export const canMoveTeam = (gameInfo, teamName, currentTime = new Date()) => {
  const lockInfo = isTeamLocked(gameInfo, teamName, currentTime);

  return {
    canMove: !lockInfo.locked,
    reason: lockInfo.locked ? lockInfo.reason : 'team_available',
    message: lockInfo.message,
    lockInfo
  };
};

/* ---------- Captain Helper Functions ---------- */

export const canBeCaptain = (team, starters, bench) => {
  if (!team) return false;

  const normalizedCaptain = typeof team === 'string' ? team : weeklyLineupUtils.normalizeTeamName(team);

  // Only check starters, not bench
  const starterTeams = starters
    .filter(team => team !== null)
    .map(team => typeof team === 'string' ? team : weeklyLineupUtils.normalizeTeamName(team));

  return starterTeams.includes(normalizedCaptain);
};

export const calculateCaptainPoints = (basePoints, isCaptain = false) => {
  const finalPoints = isCaptain ? basePoints * 2 : basePoints;
  const bonus = isCaptain ? basePoints : 0;

  return {
    basePoints,
    finalPoints,
    bonus,
    isCaptain
  };
};

/* ---------- Trip Play Helper Functions ---------- */

export const canUseTripPlay = (team, starters, bench) => {
  if (!team) return false;

  const normalizedTripPlay = typeof team === 'string' ? team : weeklyLineupUtils.normalizeTeamName(team);

  // Only check starters, not bench (same as captain)
  const starterTeams = starters
    .filter(team => team !== null)
    .map(team => typeof team === 'string' ? team : weeklyLineupUtils.normalizeTeamName(team));

  return starterTeams.includes(normalizedTripPlay);
};

export const calculateTripPlayPoints = (basePoints, isTripPlay = false) => {
  const finalPoints = isTripPlay ? basePoints * 3 : basePoints;
  const bonus = isTripPlay ? basePoints * 2 : 0; // 2x bonus (3x total)

  return {
    basePoints,
    finalPoints,
    bonus,
    isTripPlay
  };
};

export const calculateCombinedPoints = (basePoints, isCaptain = false, isTripPlay = false) => {
  if (isCaptain && isTripPlay) {
    // 5x total (2x from captain, 3x from trip play, but they multiply)
    const finalPoints = basePoints * 5;
    const bonus = basePoints * 4; // 4x bonus (5x total)
    return {
      basePoints,
      finalPoints,
      bonus,
      multiplier: '5x',
      isCaptain,
      isTripPlay
    };
  } else if (isCaptain) {
    return {
      ...calculateCaptainPoints(basePoints, true),
      multiplier: '2x',
      isTripPlay: false
    };
  } else if (isTripPlay) {
    return {
      ...calculateTripPlayPoints(basePoints, true),
      multiplier: '3x',
      isCaptain: false
    };
  } else {
    return {
      basePoints,
      finalPoints: basePoints,
      bonus: 0,
      multiplier: '1x',
      isCaptain: false,
      isTripPlay: false
    };
  }
};

/* ---------- Shared helpers ---------- */
export const toJSDate = (v) => {
  if (!v) return null;
  if (typeof v?.toDate === 'function') return v.toDate();
  if (typeof v === 'number') return new Date(v);
  const d = new Date(v);
  return isNaN(d) ? null : d;
};

export const formatGameTimeChip = (value) => {
  const date = toJSDate(value);
  if (!date) return null;
  const now = new Date();
  const isToday = date.toDateString() === now.toDateString();
  const isTomorrow = date.toDateString() === new Date(now.getTime() + 86400000).toDateString();
  if (isToday)    return `Today ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  if (isTomorrow) return `Tomorrow ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}`;
  // The weekday, not only the date. College football runs Thursday through
  // Saturday and occasionally Sunday, so which day of the week a game falls on
  // is the part a manager is actually planning around, and "Sep 5, 7:30 PM"
  // made them work it out from a calendar. Today and Tomorrow already say it
  // in plainer words, so only the dated branch needs it.
  return date.toLocaleDateString('en-US', {
    weekday: 'short', month: 'short', day: 'numeric',
    hour: 'numeric', minute: '2-digit',
  });
};

export const isWeekEditable = (week, currentWeek) => {
  return week === currentWeek;
};

// How far ahead of a kickoff a page starts polling for scores.
//
// A manager opening their lineup ten minutes before the first game has to
// start polling on its own: nothing remounts the page when the ball is
// kicked, so a poll armed only by a game already being live would never arm
// at all on that visit.
export const GAMEDAY_LEAD_MS = 60 * 60 * 1000;

// How often a page refetches scores while games are running.
//
// Matched to the backend/espn.js cron rather than set faster: nothing newer
// than the last ingestion exists to fetch, so a shorter interval only spends
// database reads to receive the same numbers again. That cost is real on a
// small instance, where the disk IO budget runs out before anything else
// does.
export const POLL_MS = 2 * 60 * 1000;

/**
 * Whether a week's games are worth polling: something is running, or a
 * kickoff is close enough that something is about to be.
 *
 * Accepts raw `games` rows. A week with nothing live and nothing imminent
 * costs a single fetch, which is every week outside a gameday.
 */
export const anyGameSoon = (games, now = Date.now()) =>
  (games || []).some((g) => {
    if (g?.game_complete) return false;
    if (g?.game_status === 'in_progress') return true;
    if (!g?.date) return false;
    const kickoff = new Date(g.date).getTime();
    if (Number.isNaN(kickoff)) return false;
    return kickoff - now <= GAMEDAY_LEAD_MS;
  });

/**
 * Whether a team row should carry its spread.
 *
 * Every state except a bye: before kickoff, while the game runs, and after it
 * finishes. Live and completed rows used to drop it on the grounds that the
 * score line said everything, but fantasy points here are largely a cover
 * result, so a team leading 7-0 against a 29.5 point line is deep in the
 * negative and a row showing the score without the line reads as though it is
 * lying. A finished game keeps it for the same reason: it is what the result
 * was graded against.
 *
 * A bye has no game and therefore no line. A game with no line posted yet
 * yields no spread string, which is a separate question this does not answer.
 */
export const shouldShowSpread = (gameDisplayInfo) =>
  Boolean(gameDisplayInfo) && gameDisplayInfo.type !== 'bye-week';
